import App from '@/App';
import { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toggleSidebar } from '@/store/slices/ThemeConfigSlice';
import Footer from './Footer';
import Header from './Header';
import Sidebar from './Sidebar';
import Portals from './Portals';
import LoadingState from '../ui/LoadingState';
import ErrorBoundary from '../ui/ErrorBoundary';

const DefaultLayout = ({ children }) => {
    const themeConfig = useSelector((state) => state.store.themeConfig);
    const dispatch = useDispatch();

    const [showLoader, setShowLoader] = useState(true);
    const [showTopButton, setShowTopButton] = useState(false);
    const [contentLoaded, setContentLoaded] = useState(false);

    const goToTop = () => {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    };

    const onScrollHandler = () => {
        if (document.body.scrollTop > 50 || document.documentElement.scrollTop > 50) {
            setShowTopButton(true);
        } else {
            setShowTopButton(false);
        }
    };

    // Handle initial loading state
    useEffect(() => {
        window.addEventListener('scroll', onScrollHandler);

        // Hide loader after a short delay regardless of other conditions
        const loaderTimer = setTimeout(() => {
            setShowLoader(false);
        }, 800); // Adjust timeout as needed
        
        // Handle screen loader elements if they exist
        const screenLoader = document.getElementsByClassName('screen_loader');
        if (screenLoader?.length) {
            screenLoader[0].classList.add('animate__fadeOut');
        }

        return () => {
            window.removeEventListener('scroll', onScrollHandler);
            clearTimeout(loaderTimer);
        };
    }, []);

    // Mark content as loaded when children are rendered
    useEffect(() => {
        if (children) {
            // Set a small delay to ensure children have time to render
            const timer = setTimeout(() => {
                setContentLoaded(true);
                setShowLoader(false);
            }, 100);
            
            return () => clearTimeout(timer);
        }
    }, [children]);
    
    return (
        <App>
            {/* BEGIN MAIN CONTAINER */}
            <div className="relative">
                {/* sidebar menu overlay */}
                <div className={`${(!themeConfig.sidebar && 'hidden') || ''} fixed inset-0 bg-[black]/60 z-50 lg:hidden`} onClick={() => dispatch(toggleSidebar())}></div>
                
                {/* screen loader - only show if content isn't loaded yet */}
                {showLoader && !contentLoaded && <LoadingState fullScreen size="large" />}
                
                <div className="fixed bottom-6 ltr:right-6 rtl:left-6 z-50">
                    {showTopButton && (
                        <button type="button" className="btn btn-outline-primary rounded-full p-2 animate-pulse bg-[#fafafa] dark:bg-[#060818] dark:hover:bg-primary" onClick={goToTop}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7l4-4m0 0l4 4m-4-4v18" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* BEGIN APP SETTING LAUNCHER */}
                {/* <Setting /> */}
                {/* END APP SETTING LAUNCHER */}

                <div className={`${themeConfig.navbar} main-container text-black dark:text-white-dark min-h-screen`}>
                    {/* BEGIN SIDEBAR */}
                    <Sidebar />
                    {/* END SIDEBAR */}

                    <div className="main-content flex flex-col min-h-screen">
                        {/* BEGIN TOP NAVBAR */}
                        <Header />
                        {/* END TOP NAVBAR */}

                        {/* BEGIN CONTENT AREA */}
                        <ErrorBoundary>
                            <Suspense fallback={<LoadingState message="Loading content..." />}>
                                <div 
                                    className={`${themeConfig.animation} p-6 animate__animated`}
                                    onLoad={() => setContentLoaded(true)} 
                                >
                                    {children}
                                </div>
                            </Suspense>
                        </ErrorBoundary>
                        {/* END CONTENT AREA */}

                        {/* BEGIN FOOTER */}
                        <Footer />
                        {/* END FOOTER */}
                        <Portals />
                    </div>
                </div>
            </div>
        </App>
    );
};

export default DefaultLayout;
